import { jwtDecode, JwtPayload } from "jwt-decode";
import moment from "moment";
import {
  Address,
  CorporateType,
  CorporateTypeTranslated,
  DocumentBusinessType,
  ForeignTaxDetailWithAddress,
  Gender,
  GenderInitials,
  MidgardQuestions,
  PersonType,
  PitbullCountry,
  PitbullProvinces,
  StepEndpoint,
} from "../types";
import heic2any from "heic2any";
import { showToastMessage } from "@allaria-tech/matter/ToastMessage";
import {
  checkIsEmailAvailable,
  customToastifyError,
  parseStateAddress,
  getDniFromCuit,
} from "../utils";
import {
  getIndividualQuestions,
  getProvinces,
  getRenaperData,
  sendStep,
  validateRenaper,
} from "../connectors/connectors";
import Fuse from "fuse.js";
import { normalizeStepsForApi } from "../steps/shared/qualifiedInvestorField";
import {
  CalendarDate,
  parseDate as parseStringToCalendarDate,
} from "@internationalized/date";
import * as Sentry from "@sentry/react";
import {
  addSendedStep,
  onUpdateCompletedStep,
  onUpdateFormData,
  onUpdateLastCompletedSubStep,
  onUpdateReachedStep,
  onUpdateStep,
  onUpdateSubStep,
} from "../reducers/onboardingDataReducer";
import {
  onUpdateCompletedStep as onUpdateCompletedStepSecurities,
  onUpdateReachedStep as onUpdateReachedStepSecurities,
  onUpdateStep as onUpdateStepSecurities,
  onUpdateSubStep as onUpdateSubStepSecurities,
} from "../reducers/securitiesReducer";
import { RenaperData, updateRenaperData } from "../reducers/renaperReducer";
import { FormikErrors, FormikValues } from "formik";
import { AppDispatch, RootState, store } from "./store";
import { getCompanyData } from "../utils/companies";

interface OurJwtPayload extends JwtPayload {
  uuid: string;
}
export const getDraftUUIDFromToken = ({ token }: { token: string }) => {
  const decoded: OurJwtPayload = jwtDecode(token);
  const uid = decoded.uuid;

  return uid;
};

//The bearer token is valid for 48 hours
export const isTokenActive = ({ token }: { token: string }) => {
  const decoded: OurJwtPayload = jwtDecode(token);
  const exp = decoded.exp;

  return exp ? moment.unix(exp).isAfter(moment()) : false;
};

export const hasExpiredToken = () => {
  const { token } = store.getState().authentication;
  return !!token && !isTokenActive({ token });
};

export const canRenewExpiredToken = () => {
  const { onboardingType } = store.getState().activeTemplate;
  return (
    hasExpiredToken() &&
    (onboardingType === "PHYSICAL" || onboardingType === "JURIDICAL")
  );
};

//This removes the step with the same type. Prevents duplicates.
export const getDraftStepsToSend = (
  steps: StepEndpoint[],
  newStep: StepEndpoint,
) => {
  const otherSteps = steps.filter(
    (step: StepEndpoint) => step["@type"] !== newStep["@type"],
  );

  return normalizeStepsForApi([...otherSteps, newStep]);
};

export const handleSendStep = async ({
  dispatch,
  personType,
  sendedSteps,
  stepId,
  step,
  email,
  onAddSentStep,
}: {
  personType: PersonType;
  sendedSteps: StepEndpoint[];
  stepId: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  step: StepEndpoint | any;
  email: string;
  dispatch: AppDispatch;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onAddSentStep?: () => Promise<void>;
}) => {
  const body = {
    companyType: getCompanyData().companyName,
    personType: personType,
    email: email,
    steps: getDraftStepsToSend(sendedSteps, step),
  };
  await sendStep({ stepId, body });
  if (onAddSentStep) return onAddSentStep();
  if (dispatch) dispatch(addSendedStep(step));
};

export const parseDate = (date: string) =>
  moment(date, ["DD/MM/YYYY", "YYYY-MM-DD"]).format("YYYY-MM-DD");

export const parseBirthDate = (date: string, splitCharacter?: string) => {
  const [year, month, day] = date.split(splitCharacter ?? "-");
  return `${day}/${month}/${year}`;
};

export const parseGenderFromInitial: Record<GenderInitials, Gender> = {
  M: "MALE",
  F: "FEMALE",
};

export const parseGenderToInitial = (gender: string) => {
  if (gender === "MALE") return "M";
  if (gender === "FEMALE") return "F";
  return "X";
};

export const parseTelephone = (telephone: string): string => {
  const digits = (telephone ?? "").replace(/^\+/, "").replace(/[\s-]/g, "");

  if (!/^\d+$/.test(digits)) return "";

  if (digits.startsWith("54")) {
    if (digits.length === 12) return `+${digits}`;
    if (digits.length === 13 && digits[2] === "9")
      return `+54${digits.slice(3)}`;
    return "";
  }

  if (digits.length === 10) return `+54${digits}`;

  return digits.length >= 9 ? `+${digits}` : "";
};

export const capitalizeString = (name: string) => {
  if (!name) return "";
  const capitalize = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  return name
    .split(" ")
    .map((substr) => capitalize(substr))
    .join(" ");
};

export const formatDNI = (dni: string): string => {
  if (!dni) return "";
  const cleanDni = dni.replace(/\D/g, ""); // Remove non-digits
  return cleanDni.length === 7 ? `0${cleanDni}` : cleanDni;
};

export const resetFields = (
  fieldsToReset: string[],
  setFieldValue: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    shouldValidate?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<FormikErrors<FormikValues>> | Promise<any>,
) => fieldsToReset.forEach((field) => setFieldValue(field, ""));

export const completeAddress = (
  addressFields: Record<string, string>,
  address: Address,
  setFieldValue: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    shouldValidate?: boolean,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<FormikErrors<FormikValues>> | Promise<any>,
  getProvincesFilteredByCountry: (country?: string) => PitbullProvinces,
) => {
  const { zipCode, state, city, municipality, street, number, country } =
    address;

  const provinces = getProvincesFilteredByCountry(country);
  const fusedProvince = fuseProvince(provinces, state, city);
  // If no records are found in FUSE, we set the first province by default to avoid breaking
  const finalProvince = fusedProvince || provinces[0].name;

  setFieldValue(addressFields.zipCode, zipCode);
  setFieldValue(addressFields.state, finalProvince);
  setFieldValue(addressFields.city, city);
  setFieldValue(addressFields.municipality, municipality);
  setFieldValue(addressFields.street, street);
  setFieldValue(addressFields.number, number);

  setFieldValue(
    addressFields.fullAddress,
    buildFullAddress(street, number, city, municipality),
  );
};

export const buildFullAddress = (
  street?: string | number | null,
  number?: string | number | null,
  city?: string | null,
  municipality?: string | null,
) => {
  const isSameAsCity =
    municipality?.trim().toLowerCase() === city?.trim().toLowerCase();

  return [street, number, city, isSameAsCity ? null : municipality]
    .filter(Boolean)
    .join(" ");
};

export const blobToFile = (blob: Blob, name: string, type: string) => {
  return new File([blob], name, {
    type: type,
  });
};

const heicMimeTypes = ["image/heif", "image/heic"];

const isHeicFile = (file: File): boolean =>
  heicMimeTypes.includes(file.type) || /\.(heic|heif)$/i.test(file.name);

export const heicToJpeg = async (file: File): Promise<File> => {
  if (!isHeicFile(file)) return file;

  const newType = "image/jpeg";

  const heicBlob = await heic2any({ blob: file, toType: newType });

  if (Array.isArray(heicBlob)) return file;

  const newFileName = file.name.replace(/\.(heic|heif)$/i, ".jpg");

  return blobToFile(heicBlob, newFileName, newType);
};

export const translateCorporate: Record<
  CorporateTypeTranslated,
  CorporateType
> = {
  associationFoundationCooperativeChamber:
    "Asociación / Cámara / Cooperativa / Fundación / Mutual",
  consortium: "Consorcio",
  trust: "Fideicomiso",
  governmentAgencyOrEntity: "Organismo y/o Entidad Gubernamental",
  other: "Otro",
  saCorporation: "SA - Sociedad Anónima",
  simplifiedJointStockCompany: "SAS - Sociedad por Acciones Simplificada",
  reciprocalGuaranteeCompany: "SGR - Sociedad de Garantía Recíproca",
  llcLimitedLiabilityCompany: "SRL - Sociedad de Responsabilidad Limitada",
};

export const translateCorporateReversed = Object.fromEntries(
  Object.entries(translateCorporate).map(([key, value]) => [value, key]),
) as Record<CorporateType, CorporateTypeTranslated>;

export const businessDocuments: Record<string, DocumentBusinessType> = {
  balance: "BALANCE",
  worthStatement: "WORTH_STATEMENT",
  sharesRegister: "SHARES_REGISTER",
  other: "OTHERS",
  statute: "STATUTE",
  poa: "POA",
  authorities: "AUTHORITIES",
  membersNote: "MEMBERS_NOTE",
  shareholdersNote: "SHAREHOLDERS_NOTE",
  trustNote: "TRUST_NOTE",
  partnersNote: "PARTNERS_NOTE",
  partnersList: "PARTNERS_LIST",
  uifInscriptionCertificate: "UIF_INSCRIPTION_CERTIFICATE",
  expenseLiquidation: "EXPENSE_LIQUIDATION",
  administratorDesignationSas: "AUTHORITIES",
  administratorDesignationConsortium: "AUTHORITIES",
  horizontalPropertyRegulationsScanned: "HORIZONTAL_PROPERTY_REGULATIONS",
  trustConstitutionContract: "TRUST_CONSTITUTION_CONTRACT",
  trusteeDocumentationStatute: "STATUTE",
  trusteeDocumentationAuthorities: "AUTHORITIES",
  coownersList: "CO_OWNER_LIST",
};

export const mapDocumentBusinessType = (type: string) =>
  businessDocuments[type];

export const mapDocumentBusinessTypeFromValue = (type: DocumentBusinessType) =>
  Object.keys(businessDocuments).find(
    (key) => businessDocuments[key] === type,
  ) as keyof typeof businessDocuments;

export const getBusinessDocumentsByCorporateType = (
  corporateType: CorporateTypeTranslated,
): DocumentBusinessType[] => {
  switch (corporateType) {
    case "saCorporation":
      return [
        "STATUTE",
        "POA",
        "BALANCE",
        "SHARES_REGISTER",
        "AUTHORITIES",
        "SHAREHOLDERS_NOTE",
        "OTHERS",
      ];
    case "llcLimitedLiabilityCompany":
      return ["STATUTE", "POA", "BALANCE", "SHAREHOLDERS_NOTE", "OTHERS"];
    case "associationFoundationCooperativeChamber":
      return [
        "STATUTE",
        "POA",
        "BALANCE",
        "AUTHORITIES",
        "MEMBERS_NOTE",
        "OTHERS",
      ];
    case "consortium":
      return [
        "HORIZONTAL_PROPERTY_REGULATIONS",
        "POA",
        "AUTHORITIES",
        "EXPENSE_LIQUIDATION",
        "OTHERS",
      ];
    case "trust":
      return [
        "TRUST_CONSTITUTION_CONTRACT",
        "POA",
        "BALANCE",
        "UIF_INSCRIPTION_CERTIFICATE",
        "TRUST_NOTE",
        "AUTHORITIES",
        "STATUTE",
        "OTHERS",
      ];
    case "governmentAgencyOrEntity":
      return ["STATUTE", "POA", "BALANCE", "AUTHORITIES", "OTHERS"];
    case "other":
      return [
        "STATUTE",
        "AUTHORITIES",
        "WORTH_STATEMENT",
        "SHAREHOLDERS_NOTE",
        "OTHERS",
      ];
    case "reciprocalGuaranteeCompany":
      return [
        "STATUTE",
        "POA",
        "BALANCE",
        "AUTHORITIES",
        "PARTNERS_NOTE",
        "PARTNERS_LIST",
        "OTHERS",
      ];
    case "simplifiedJointStockCompany":
      return [
        "STATUTE",
        "POA",
        "BALANCE",
        "AUTHORITIES",
        "SHAREHOLDERS_NOTE",
        "OTHERS",
      ];
    default:
      return [];
  }
};

export const getDocumentTypeParsed = (
  corporateType: CorporateTypeTranslated,
  documentType: DocumentBusinessType,
) => {
  switch (documentType) {
    case "BALANCE":
      return "Balance certificado por CPCE o doc. patrimonial";
    case "WORTH_STATEMENT":
      return "Estado de situación patrimonial certificado por CPCE";
    case "SHARES_REGISTER":
      return "Registro de accionistas";
    case "OTHERS":
      return "Otro";
    case "STATUTE":
      if (corporateType === "consortium")
        return "Reglamento de propiedad horizontal escaneado";
      if (corporateType === "trust")
        return "Contrato de constitución del Fideicomiso";
      return "Estatuto o contrato social escaneado";
    case "POA":
      return "Poder de los firmantes";
    case "AUTHORITIES":
      if (
        corporateType === "consortium" ||
        corporateType === "simplifiedJointStockCompany"
      )
        return "Acta de designación de administrador";
      return "Acta de designación de autoridades";
    case "MEMBERS_NOTE":
      return "Nota de miembros";
    case "SHAREHOLDERS_NOTE":
      return "Nota de accionistas y beneficiarios finales";
    case "TRUST_NOTE":
      return "Nota de fiduciantes, fiduciarios y beneficiarios";
    case "PARTNERS_NOTE":
      return "Nota de socios protectores y beneficiarios";
    case "PARTNERS_LIST":
      return "Nómina de socios";
    case "UIF_INSCRIPTION_CERTIFICATE":
      return "Constancia de inscripción ante UIF";
    case "EXPENSE_LIQUIDATION":
      return "Liquidación de expensas";
    case "HORIZONTAL_PROPERTY_REGULATIONS":
      return "Reglamento de propiedad horizontal escaneado";
    case "TRUST_CONSTITUTION_CONTRACT":
      return "Contrato de constitución del Fideicomiso";
    default:
      return "";
  }
};

export const replaceSpecialCharacters = (text: string) => {
  return text
    .normalize("NFD") // Decompose accented characters
    .replace(/[\u0300-\u036f]/g, "") // Remove accent marks
    .replace(/[()]/g, ""); // Remove parentheses
};

export const toTestId = (text: string) =>
  replaceSpecialCharacters(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const parseRegistrationAuthority = (authority: string) => {
  switch (authority) {
    case "OTHER":
      return "Otro";
    case "DIRECTORATE_OF_LEGAL_ENTITIES":
      return "Dirección de Personas Jurídicas";
    case "GENERAL_INSPECTION_OF_JUSTICE":
      return "Inspección General de Justicia";
    default:
      return "";
  }
};

export const getForeignTaxDetails = (
  foreignTaxDetailBool: string,
  foreignTaxDetail?: {
    idNumber: string;
  } & ForeignTaxDetailWithAddress["address"],
) =>
  foreignTaxDetailBool === "SI" && foreignTaxDetail
    ? {
        foreignTaxDetail: {
          idNumber: String(foreignTaxDetail.idNumber).trim(),
          address: {
            street: foreignTaxDetail.street,
            number: foreignTaxDetail.number,
            zipCode: foreignTaxDetail.zipCode,
            city: foreignTaxDetail.city,
            state: foreignTaxDetail.state,
            country: foreignTaxDetail.country,
            floor: foreignTaxDetail.floor,
            apartment: foreignTaxDetail.apartment,
            municipality: foreignTaxDetail.municipality,
          },
        },
      }
    : {};

export const showCustomToastifyError = (error: string) => {
  let errorDescription = "";

  if (error === "CXU_NOT_OWNER")
    errorDescription = "La cuenta ingresada no está asociada a tu CUIT/CUIL.";
  if (error === "CXU_NOT_FOUND")
    errorDescription =
      "CBU/CVU no existente, por favor revisá los datos ingresados.";
  if (error === "DNI_NO_VIGENTE")
    errorDescription =
      "El DNI ingresado no es vigente. Por favor revisá los datos ingresados.";
  if (error === "INVALID_DATA_DNI")
    errorDescription =
      "Los datos ingresados no coinciden con los registros oficiales. Por favor, verificá que estén correctos";
  if (error === "CUIT_NOT_CONFIRMED")
    errorDescription =
      "No pudimos validar tu CUIT. Revisalo e intentá de nuevo.";
  if (error === "DRAFT_BASE_MISMATCH")
    errorDescription =
      "El mail, CUIT/CUIL o referido no coinciden con los que ingresaste al iniciar el registro. Verificá que los datos sean correctos.";
  return showToastMessage(customToastifyError(errorDescription), {
    containerId: "formAlert",
    type: "ERROR",
  });
};

export const getQuestions = async (): Promise<MidgardQuestions> => {
  try {
    const { data } = await getIndividualQuestions({
      company: getCompanyData().companyName,
    });
    return data;
  } catch (error) {
    throw new Error("Error al obtener las preguntas");
  }
};

export const getProvincesByIso = async (
  iso?: string,
): Promise<PitbullProvinces> => {
  try {
    const { data } = await getProvinces(iso);
    return data;
  } catch (error) {
    throw new Error("Error al obtener las preguntas");
  }
};

export const fuseProvince = (
  provinces: PitbullProvinces,
  state: string,
  city: string,
): string => {
  const fuse = new Fuse(provinces, {
    useExtendedSearch: true,
    keys: ["name"],
    threshold: 0.3, // This is the score, beyond 0.3 it is not very accurate
  });
  const parsedState = parseStateAddress(state);

  // Search by administrative_area_level_1
  let fuseLogs = fuse.search(parsedState);

  if (fuseLogs.length === 0) {
    // Search by administrative_area_level_2
    fuseLogs = fuse.search(city);
  }

  // If no records are found in FUSE, we set the first province by default to avoid breaking
  return fuseLogs[0]?.item.name || provinces[0].name;
};

export const getParsedEmailFromUrl = (): string => {
  const params = new URLSearchParams(window.location.search);
  let email = params.get("email");
  if (email) {
    email = decodeURIComponent(email).replace(/ /g, "+");
  }
  return email || "";
};

export const stringToCalendarDate = (
  dateString: string,
): CalendarDate | null => {
  try {
    if (!dateString) return null;

    let [year, month, day]: Array<string | number> = dateString
      .split("-")
      .map(Number);

    year = year.toString().padStart(4, "0");
    month = month.toString().padStart(2, "0");
    day = day.toString().padStart(2, "0");

    const calendarDate = parseStringToCalendarDate(`${year}-${month}-${day}`);

    return calendarDate;
  } catch (error) {
    console.error("Error parsing date", error);
    return null;
  }
};

// Convert CalendarDate to string format YYYY-MM-DDDD
export const calendarDateToString = (date: CalendarDate): string => {
  const day = date.day.toString().padStart(2, "0");
  const month = date.month.toString().padStart(2, "0");
  const year = date.year;
  return `${year}-${month}-${day}`;
};

/**
 * Converts a File or Blob to a Base64 string
 * @param {File | Blob} file - The file to convert
 * @returns {Promise<string>} Promise resolving to the Base64 string
 */
export const fileToBase64 = (file: File | Blob): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject(new Error("FileReader did not return a string"));
      }
    };

    reader.onerror = () => {
      reject(new Error("No se pudo leer el archivo seleccionado"));
    };

    reader.onabort = () => {
      reject(new Error("La lectura del archivo fue cancelada"));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Converts a Base64 string back to a File object
 * @param {string} base64String - The Base64 string to convert
 * @param {string} filename - The name to give the resulting file
 * @param {string} [mimeType] - The MIME type (defaults to image/png if not provided)
 * @returns {File} The resulting File object
 */
export const base64ToFile = (
  base64String: string,
  filename: string,
  mimeType: string = "image/png",
): File => {
  // Extract the Base64 data part if it's a data URL
  const base64Data = base64String.includes("base64,")
    ? base64String.split("base64,")[1]
    : base64String;

  // Decode the Base64 data
  const byteString = atob(base64Data);

  // Create an array buffer
  const arrayBuffer = new ArrayBuffer(byteString.length);
  const uint8Array = new Uint8Array(arrayBuffer);

  // Fill the array with the decoded bytes
  for (let i = 0; i < byteString.length; i++) {
    uint8Array[i] = byteString.charCodeAt(i);
  }

  // Create a Blob with the array buffer
  const blob = new Blob([arrayBuffer], { type: mimeType });

  // Create a File from the Blob
  return new File([blob], filename, { type: mimeType });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleCatchRenaper = (error: any) => {
  if (error && typeof error === "object" && "response" in error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Sentry.captureException((error as any).response);
  } else {
    Sentry.captureException(error);
  }
  if (error instanceof Error) {
    // Type guard for 'code' property
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((error as any).code === "ERR_BAD_RESPONSE") {
      throw new Error("INVALID_DATA_DNI");
    }
    throw new Error(error.message);
  } else {
    throw new Error(String(error));
  }
};

export const getRenaperInformation = async () => {
  const { data } = await getRenaperData();
  const validation = data?.validations?.[0];

  if (!validation?.information) {
    throw new Error("FAILED at get renaper data - no validations");
  }

  const informationParsed = JSON.parse(validation.information) as RenaperData;

  if (!informationParsed.procedureId) {
    const orderNumber = parseDniScanOrderNumber(validation.statusDetail);
    if (orderNumber) informationParsed.procedureId = orderNumber;
  }

  return {
    informationParsed,
    externalCode: validation.externalCode,
  };
};

const parseDniScanOrderNumber = (statusDetail?: string) => {
  if (!statusDetail) return undefined;
  try {
    const orderNumber = JSON.parse(statusDetail)?.dniData?.orderNumber;
    return typeof orderNumber === "string" && orderNumber
      ? orderNumber
      : undefined;
  } catch {
    return undefined;
  }
};

export const onContinueRenaper = async (
  values: FormikValues,
  dispatch: AppDispatch,
  state: RootState,
  countries: PitbullCountry[],
  addressKey?: "addresses" | "representativesAddresses",
) => {
  try {
    const { gender, tramitNumber, identityDocument } = values;
    const { data } = await validateRenaper({
      gender,
      order: tramitNumber,
      number: identityDocument,
    });

    const failedStatus = ["FAILED", "CHECK"];

    // TYPES OF STATUS "OK" | "FAILED" | "CHECK"
    if (!data || !data.status || failedStatus.includes(data.status)) {
      Sentry.captureException(new Error(), {
        extra: { status: data?.status },
      });

      if (data?.status === "CHECK") {
        const { data: renaperData } = await getRenaperData();
        const statusDetail = JSON.parse(
          renaperData.validations[0]?.statusDetail,
        );
        if (
          statusDetail.code === "10001" &&
          statusDetail.message === "Exito" &&
          statusDetail.valid === "No vigente"
        ) {
          throw new Error("DNI_NO_VIGENTE");
        }
      }
      throw new Error("FAILED at validate Renaper");
    } else if (data.status === "OK") {
      const { informationParsed, externalCode } = await getRenaperInformation();

      const { expirationDate } = informationParsed;
      if (moment(expirationDate, "DD-MM-YYYY").isBefore(moment(), "day")) {
        throw new Error("DNI_NO_VIGENTE");
      }

      dispatch(
        updateRenaperData({
          ...informationParsed,
          externalCode,
        }),
      );

      await checkIsEmailAvailable({
        dispatch,
        idNumber: values.identityDocument,
      });

      const body = getParsedRenaperData(
        values,
        state,
        informationParsed,
        countries,
        addressKey,
      );
      dispatch(onUpdateFormData({ ...body }));
      return { newValuesToForm: body };
    }
  } catch (error) {
    handleCatchRenaper(error);
  }
};

export const getParsedRenaperData = (
  values: FormikValues,
  state: RootState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  informationParsed: any,
  countries: PitbullCountry[],
  addressKey: "addresses" | "representativesAddresses" = "addresses",
) => {
  const {
    names,
    lastNames,
    birthdate,
    countryBirth,
    streetAddress,
    numberStreet,
    country,
    city,
    number,
    nationality,
    municipality,
    gender,
    procedureId,
  } = informationParsed;

  const { isExternalInformation } = state.onboardingData;

  const parsedCitizenship = countries.find(({ citizenship }) => {
    const lowerCaseCitizenship = citizenship?.toLowerCase();

    // Typo in Renaper API
    if (nationality === "COSTARICENSE") {
      return "costarricense" === lowerCaseCitizenship;
    }
    if (nationality === "SUDAFRICANA") {
      return "surafricana" === lowerCaseCitizenship;
    }

    return nationality?.toLowerCase() === lowerCaseCitizenship;
  })?.citizenship;

  const countryBirthParsed = getParsedCountry(
    countries,
    countryBirth,
    country,
    nationality,
  );

  const getAddress = () => {
    let cityParsed = city;
    let municipalityParsed = municipality;
    if (city === "SIN INFORMAR") cityParsed = "";
    if (municipality === "SIN INFORMAR") municipalityParsed = "";
    const fullAddress = buildFullAddress(
      streetAddress,
      numberStreet,
      cityParsed,
      municipalityParsed,
    );

    // Just for juridical
    if (addressKey === "representativesAddresses") {
      if (
        !isExternalInformation ||
        (isExternalInformation && !values[addressKey]?.street)
      ) {
        return {
          birthCountry: countryBirthParsed,
          [addressKey]: {
            fullAddress,
            country: getParsedCountry(countries, country, country, nationality),
          },
        };
      }
    } else {
      // Physical and signatory
      if (!isExternalInformation) {
        return {
          birthCountry: countryBirthParsed,
          [addressKey]: {
            fullAddress,
            country: getParsedCountry(countries, country),
            street: streetAddress,
            number: numberStreet,
            city: cityParsed,
            municipality: municipalityParsed,
          },
        };
      }
    }
    return {};
  };

  const body = {
    name: names,
    lastName: capitalizeString(lastNames),
    birthDate: birthdate,
    citizenship: parsedCitizenship,
    gender,
    identityDocument: number || getDniFromCuit(values.cuit), // If renaper is not available, we use the CUIT to get the DNI
    ...(procedureId ? { tramitNumber: procedureId } : {}),
    ...getAddress(),
  };

  return body;
};

const RENAPER_TO_PITBULL_COUNTRY_MAP: Record<string, string> = {
  TERRITORIO_CHINO_de_TAIWAN: "taiwan",
  REPUBLICA_POPULAR_CHINA: "china",
  HOLANDA: "paises bajos",
  CONGO: "republica democratica del congo"
};

const getParsedCountry = (
  countries: PitbullCountry[],
  countryToFind?: string,
  defaultCountry?: string,
  nationality?: string,
) => {
  //YUGOSLAVIA case: it does not exists in Pitbull microservice, so the
  // match falls back to the user's nationality (citizenship) instead.
  if (countryToFind === "YUGOSLAVIA") {
    return countries.find(
      ({ citizenship }) => citizenship === nationality?.toLowerCase(),
    )?.name;
  }

  // RENAPER countries use uppercase words joined by underscores.
  // Normalize underscores to spaces (and map known exceptions) before matching.
  const parsedCountry = (
    countryToFind
      ? (RENAPER_TO_PITBULL_COUNTRY_MAP[countryToFind] ?? countryToFind)
      : defaultCountry || ""
  ).replace(/_/g, " ");

  // Ignore accents and casing to always find a match
  const countryFound = countries.find(({ name }) => {
    return (
      name.localeCompare(parsedCountry, "es", {
        sensitivity: "base",
      }) === 0
    );
  });

  return countryFound?.name;
};

export const goToStep = ({
  dispatch,
  step,
  substep,
}: {
  dispatch: AppDispatch;
  step: number;
  substep?: number;
}) => {
  const state = store.getState();
  const isSecurities = state.activeTemplate?.onboardingType === "SECURITIES";

  if (isSecurities) {
    dispatch(onUpdateStepSecurities(step));
    dispatch(onUpdateReachedStepSecurities(step));
    if (step > 1) dispatch(onUpdateCompletedStepSecurities(step - 1));
    substep && dispatch(onUpdateSubStepSecurities(substep));
  } else {
    dispatch(onUpdateStep(step));
    dispatch(onUpdateReachedStep(step));
    if (step > 1) dispatch(onUpdateCompletedStep(step - 1));
    substep && dispatch(onUpdateSubStep(substep));

    const isNewStep = state.onboardingData.stepData.step !== step;
    if (isNewStep) dispatch(onUpdateLastCompletedSubStep(0));
  }
};

export { isDev } from "./env";
